/**
 * Block: Front page hero
 *
 * Scroll-scrubbed hero. As the user scrolls, a pre-decoded image sequence
 * (extracted offline from the descent footage) is painted to a <canvas>.
 * ScrollTrigger only pins the section and reports raw scroll progress; the
 * frame index is driven by an eased playhead (see _tick) that coasts toward the
 * scroll position and settles in the direction you scrolled after you stop — a
 * monotonic ease, so it never springs back past the target. The intro and CMS
 * text groups live in a DOM overlay and are revealed from the same eased value.
 *
 * We deliberately never scrub <video>.currentTime — iOS Safari re-decodes from
 * the nearest keyframe on every seek and stutters. Pre-decoded ImageBitmap
 * blits stay smooth everywhere.
 *
 * Frames are static theme assets under /static/hero/, rebuilt with
 * bin/build-hero-frames.sh. The markup is rendered server-side; in the block
 * editor (is_preview) and under prefers-reduced-motion the static stacked
 * layout is shown instead — no pin, no scrub (see the .twig + .is-static SCSS).
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// iOS/Android fire a window 'resize' every time the address bar shows/hides. Let GSAP
// skip the internal refresh those cause; our own _resize applies the same guard. Without
// this the pinned hero re-pins mid-scroll and the rest of the page fights the user's
// scroll on touch.
ScrollTrigger.config({ ignoreMobileResize: true });

const reducedMotion = () =>
  !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);

// Hermite smoothstep: eased 0→1 ramp between edges a and b.
const smoothstep = (a, b, x) => {
  if (a === b) return x < a ? 0 : 1;
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};

class FrontPageHero {
  constructor(section) {
    this.section = section;
    this.stage = section.querySelector('.front-page-hero__stage');
    this.canvas = section.querySelector('[data-fph-canvas]');
    this.poster = section.querySelector('.front-page-hero__poster');
    this.intro = section.querySelector('[data-fph-intro]');
    this.groups = Array.from(section.querySelectorAll('[data-fph-group]'));

    this.ext = section.dataset.frameExt || 'webp';

    // Two frame sets. The full-res desktop set (100×2160²) decodes to ~1.9 GB of
    // ImageBitmaps held at once — fine on desktop, but it kills the tab on iOS. Touch
    // devices (incl. iPad in landscape, which is ≥1200px) and low-memory devices get a
    // lighter set + shorter pin + snappier damping + lower DPR; see _pickSet. The
    // 16:9↔1:1 crop (this.targetAspect below) stays on the 1200px query independently.
    this.mqlDesktop = window.matchMedia ? window.matchMedia('(min-width: 1200px)') : { matches: true };
    this.baseDesktop = section.dataset.framesBase || '';
    this.baseMobile = section.dataset.framesBaseMobile || this.baseDesktop;
    this.countDesktop = parseInt(section.dataset.frameCount, 10) || 0;
    this.countMobile = parseInt(section.dataset.frameCountMobile, 10) || this.countDesktop;
    this.scrollDistance = parseInt(section.dataset.scrollDistance, 10) || 300;
    this._pickSet(); // sets base, count, screens, tau, dpr, useMobileSet for this device

    this.ctx = this.canvas ? this.canvas.getContext('2d') : null;
    this.frames = [];
    this.frameState = { i: 0 };
    this._lastDrawnIdx = -1; // _tick re-blits only when the rounded frame index changes
    this.bgColor = { r: 2, g: 2, b: 38 };

    // Cover-crop aspect: 16:9 on big screens, 1:1 below (matches the SCSS 1200px
    // breakpoint). The square source is cropped to this and cover-drawn; see
    // _drawCover. Recomputed on resize (crossing the breakpoint fires resize).
    this.targetAspect = this.mqlDesktop.matches ? 16 / 9 : 1;

    // Crop focal point (object-position semantics, 0..1): the falling node sits on
    // the RIGHT of the square footage, so the tight 1:1 mobile crop needs to bias
    // right to keep it. focusX = p places source point p at p of the viewport, so
    // it's always visible on any aspect. focusX drives the horizontal window;
    // focusY the vertical (16:9) source crop. Author via data-focus-x/y (0-100).
    const fx = parseFloat(section.dataset.focusX);
    const fy = parseFloat(section.dataset.focusY);
    this.focusX = Number.isFinite(fx) ? clamp01(fx / 100) : 0.5;
    this.focusY = Number.isFinite(fy) ? clamp01(fy / 100) : 0.5;

    // Playhead smoothing. Scroll sets `targetP` (0..1); each frame `currentP`
    // eases toward it with an exponential lerp. It's monotonic (never crosses
    // the target), so the video keeps coasting toward where you scrolled and
    // decelerates to rest — always settling in the scrolled direction, with no
    // spring-back. `tau` = smoothing time constant in seconds (higher = more
    // coast/lag); tunable via data-smooth.
    this.targetP = 0;
    this.currentP = 0;
    this.raf = null;
    this.lastTime = 0;
    this.fade = 0.12;
    this.groupWindows = [];
    this._lastW = window.innerWidth;
    this._lastH = window.innerHeight;

    this._resize = this._resize.bind(this);
    this._render = this._render.bind(this);
    this._tick = this._tick.bind(this);
    this._onVisibility = this._onVisibility.bind(this);
  }

  // Choose the frame set + motion budget for this device. Any touchscreen (phone,
  // iPad — including landscape, which is ≥1200px — Android, touch laptop) or a
  // low-memory device gets the lighter mobile set so it never tries to hold the
  // desktop set's ~1.9 GB of decoded frames. Also shortens the pin, snappier damping
  // and a lower DPR cap so the pinned descent doesn't monopolise/jank touch scrolling.
  // Called once at construction; the chosen set is frozen for the session (no runtime
  // swap — see _resize), while dpr/targetAspect still adapt on resize.
  _pickSet() {
    const isTouch = (navigator.maxTouchPoints || 0) > 0 || 'ontouchstart' in window;
    const lowMem = !!(navigator.deviceMemory && navigator.deviceMemory <= 4);
    this.useMobileSet = !this.mqlDesktop.matches || isTouch || lowMem;
    const m = this.useMobileSet;
    this.base = m ? this.baseMobile : this.baseDesktop;
    this.count = m ? this.countMobile : this.countDesktop;
    this.screens = m ? Math.min(this.scrollDistance / 100, 1.75) : this.scrollDistance / 100;
    const tau = Number.isFinite(parseFloat(this.section.dataset.smooth)) ? parseFloat(this.section.dataset.smooth) : 0.25;
    this.tau = m ? Math.min(tau, 0.12) : tau;
    this.dpr = Math.min(window.devicePixelRatio || 1, m ? 1.5 : 2);
  }

  init() {
    if (!this.ctx || !this.count || !this.base) return;

    // No pin / no scrub: stack everything and show poster + all text groups.
    if (reducedMotion()) {
      this.section.classList.add('is-static');
      this.groups.forEach((g) => g.classList.add('is-active'));
      return;
    }

    this.bgColor = this._readBg();
    this._sizeCanvas();
    window.addEventListener('resize', this._resize);

    this._loadFrames().then(() => {
      this._render(); // paint frame 0 under the (fading) poster
      this.section.classList.add('is-ready');
      this._setupScroll();
      ScrollTrigger.refresh();
    });
  }

  _frameUrl(i) {
    const n = String(i + 1).padStart(3, '0');
    return `${this.base}f_${n}.${this.ext}`;
  }

  // Load frames through a bounded worker pool instead of firing all N fetch+decode
  // calls at once. A storm of ~100 concurrent createImageBitmap decodes spikes memory
  // and saturates the connection pool — on iOS that alone can hang or kill the tab.
  // We resolve as soon as frame 0 is decoded so the descent goes interactive, then
  // stream the rest in the background from the start (the scroll direction), so the
  // frame the user is looking at loads soonest. _frameAt clamps to the nearest decoded
  // frame while the tail streams in.
  _loadFrames() {
    const supportsBitmap = 'createImageBitmap' in window;
    const load = (i) =>
      fetch(this._frameUrl(i))
        .then((r) => r.blob())
        .then((b) => (supportsBitmap ? createImageBitmap(b) : this._blobToImage(b)))
        .then((img) => { this.frames[i] = img; })
        .catch(() => { /* leave a hole; _frameAt clamps to the nearest loaded frame */ });

    const concurrency = this.useMobileSet ? 3 : 6;
    return load(0).then(() => {
      let cursor = 1;
      const worker = () => (cursor >= this.count ? Promise.resolve() : load(cursor++).then(worker));
      // Fire-and-forget the pool; callers only await frame 0.
      for (let k = 0; k < concurrency; k++) worker();
    });
  }

  _blobToImage(blob) {
    return new Promise((res, rej) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);
      img.onload = () => { URL.revokeObjectURL(url); res(img); };
      img.onerror = () => { URL.revokeObjectURL(url); rej(); };
      img.src = url;
    });
  }

  _sizeCanvas() {
    const r = this.stage.getBoundingClientRect();
    this.canvas.width = Math.round(r.width * this.dpr);
    this.canvas.height = Math.round(r.height * this.dpr);
    this.canvas.style.width = r.width + 'px';
    this.canvas.style.height = r.height + 'px';
  }

  _readBg() {
    const fallback = { r: 2, g: 2, b: 38 };
    try {
      const m = getComputedStyle(this.section).backgroundColor.match(/\d+/g);
      if (m && m.length >= 3) return { r: +m[0], g: +m[1], b: +m[2] };
    } catch (e) { /* keep fallback */ }
    return fallback;
  }

  _bg(a) {
    const c = this.bgColor;
    return `rgba(${c.r}, ${c.g}, ${c.b}, ${a})`;
  }

  // Nearest already-decoded frame to idx (defensive — by scrub time all are in).
  _frameAt(idx) {
    if (this.frames[idx]) return this.frames[idx];
    let lo = idx, hi = idx;
    while (lo >= 0 || hi < this.count) {
      if (lo >= 0 && this.frames[lo]) return this.frames[lo];
      if (hi < this.count && this.frames[hi]) return this.frames[hi];
      lo--; hi++;
    }
    return null;
  }

  // Draw a frame cover-cropped to the current target aspect (16:9 desktop / 1:1
  // mobile), sized to FILL the canvas height and centred horizontally. The source
  // is square, so we first crop it to the target aspect. Because height always
  // fills, any residual gap can only ever be on the sides (ultra-wide viewports),
  // where the caller feathers it into the background. Returns the drawn rect.
  _drawCover(img) {
    const ctx = this.ctx;
    const cw = this.canvas.width;
    const ch = this.canvas.height;
    const a = this.targetAspect;

    // Crop the source to the target aspect, biased by the focal point.
    const srcAspect = img.width / img.height;
    let sx, sy, sw, sh;
    if (srcAspect > a) { // source wider than target → trim its sides
      sh = img.height;
      sw = sh * a;
      sx = (img.width - sw) * this.focusX;
      sy = 0;
    } else { // source taller than target → trim its top/bottom
      sw = img.width;
      sh = sw / a;
      sx = 0;
      sy = (img.height - sh) * this.focusY;
    }

    // Fill the canvas height; width follows the target aspect, positioned by the
    // focal point. When the width overflows the canvas (the common case) focusX
    // chooses which slice shows; when it falls short (viewport wider than the
    // target aspect) dx ≥ 0 and the caller feathers the gap.
    const dh = ch;
    const dw = ch * a;
    const dx = (cw - dw) * this.focusX;
    const dy = 0;
    ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
    return { dx, dy, dw, dh };
  }

  // Fade the video's letterboxed edges into the background colour so the hard
  // frame edge isn't visible where the 1:1 video doesn't reach the viewport.
  _featherEdges({ dx, dy, dw, dh }) {
    const ctx = this.ctx;
    if (dx > 0.5) { // pillarbox → feather left & right
      const fw = Math.round(dw * 0.15);
      let g = ctx.createLinearGradient(dx, 0, dx + fw, 0);
      g.addColorStop(0, this._bg(1)); g.addColorStop(1, this._bg(0));
      ctx.fillStyle = g; ctx.fillRect(dx, dy, fw, dh);
      g = ctx.createLinearGradient(dx + dw - fw, 0, dx + dw, 0);
      g.addColorStop(0, this._bg(0)); g.addColorStop(1, this._bg(1));
      ctx.fillStyle = g; ctx.fillRect(dx + dw - fw, dy, fw, dh);
    }
    if (dy > 0.5) { // letterbox → feather top & bottom
      const fh = Math.round(dh * 0.15);
      let g = ctx.createLinearGradient(0, dy, 0, dy + fh);
      g.addColorStop(0, this._bg(1)); g.addColorStop(1, this._bg(0));
      ctx.fillStyle = g; ctx.fillRect(dx, dy, dw, fh);
      g = ctx.createLinearGradient(0, dy + dh - fh, 0, dy + dh);
      g.addColorStop(0, this._bg(0)); g.addColorStop(1, this._bg(1));
      ctx.fillStyle = g; ctx.fillRect(dx, dy + dh - fh, dw, fh);
    }
  }

  _render() {
    const ctx = this.ctx;
    if (!ctx) return;

    // Render a single crisp frame (nearest to the eased scrub position). The
    // smoothness comes from damping the *scroll* — GSAP scrub eases the playhead
    // toward the scroll position — NOT from blending frames. Cross-dissolving
    // adjacent frames showed a visible double image, so we don't do that.
    const idx = Math.round(Math.max(0, Math.min(this.frameState.i, this.count - 1)));
    const img = this._frameAt(idx);
    if (!img) return;

    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    const rect = this._drawCover(img);
    this._featherEdges(rect);
  }

  // Pre-compute each text group's reveal window (as a fraction of the descent).
  // Groups distribute evenly after the intro clears; a data-enter (0-100) on a
  // group overrides its entry point. The last group stays visible to the end.
  _computeGroupWindows() {
    const n = this.groups.length;
    const start = 0.2;
    const end = 0.98;
    const span = (end - start) / Math.max(n, 1);
    this.fade = Math.min(span * 0.5, 0.14);
    this.groupWindows = this.groups.map((el, i) => {
      const isFirst = i === 0;
      const de = parseFloat(el.dataset.enter);
      const authored = Number.isFinite(de) ? clamp01(de / 100) : null;
      // The descent now begins AFTER a static surface band the visitor scrolls down
      // into, so the first group must already be visible at p=0. fadeIn is
      // smoothstep(enter, enter+fade, p), which is 1 only once p ≥ enter+fade — so the
      // first group's ramp starts negative (its upper edge sits at/before 0). An
      // authored data-enter still shifts it (its fade completes at the authored point).
      const enter = isFirst
        ? (authored != null ? authored - this.fade : -this.fade)
        : (authored != null ? authored : start + i * span);
      // First group holds until the second group's default entry, then cross-fades out;
      // the last (or only) group holds to the end.
      const exit = isFirst ? (n > 1 ? start + span : 1.2) : (i < n - 1 ? enter + span : 1.2);
      return { el, enter, exit };
    });
  }

  _setupScroll() {
    this._computeGroupWindows();
    this._updateText(0);

    // ScrollTrigger does the pinning + reports raw progress only. The actual
    // playhead easing happens in our spring (_tick) — not via scrub.
    this.st = ScrollTrigger.create({
      trigger: this.section,
      start: 'top top',
      end: () => '+=' + window.innerHeight * this.screens,
      pin: true,
      invalidateOnRefresh: true,
      onRefresh: (self) => { this.targetP = self.progress; this._wake(); },
      onUpdate: (self) => { this.targetP = self.progress; this._wake(); },
    });

    document.addEventListener('visibilitychange', this._onVisibility);
  }

  _wake() {
    if (this.raf == null) {
      this.lastTime = 0;
      this.raf = requestAnimationFrame(this._tick);
    }
  }

  _tick(now) {
    const dt = this.lastTime ? Math.min((now - this.lastTime) / 1000, 0.05) : 1 / 60;
    this.lastTime = now;

    // Exponential approach toward the scroll target. Monotonic — never crosses
    // the target — so the playhead always settles in the scrolled direction.
    this.currentP += (this.targetP - this.currentP) * (1 - Math.exp(-dt / this.tau));

    const p = clamp01(this.currentP);
    this.frameState.i = p * (this.count - 1);
    // Re-blit only when the rounded frame index actually changes. During the settle
    // tail (and any sub-frame drift) this skips redundant identical canvas draws,
    // keeping the main thread free so scrolling elsewhere on the page stays fluid.
    const idx = Math.round(Math.max(0, Math.min(this.frameState.i, this.count - 1)));
    if (idx !== this._lastDrawnIdx) { this._render(); this._lastDrawnIdx = idx; }
    this._updateText(p);

    if (Math.abs(this.targetP - this.currentP) < 0.0004) {
      this.currentP = this.targetP;
      this.raf = null;
    } else {
      this.raf = requestAnimationFrame(this._tick);
    }
  }

  // Drive the intro fade-out and the text-group reveals from the eased playhead,
  // so the copy shares the same momentum/settle as the video.
  _updateText(p) {
    if (this.intro) {
      const a = 1 - smoothstep(0.03, 0.15, p);
      this.intro.style.opacity = a;
      this.intro.style.transform = `translateY(${(1 - a) * -40}px)`;
      this.intro.style.visibility = a < 0.01 ? 'hidden' : 'visible';
    }
    for (const w of this.groupWindows) {
      const fadeIn = smoothstep(w.enter, w.enter + this.fade, p);
      const fadeOut = 1 - smoothstep(w.exit - this.fade, w.exit, p);
      const a = Math.min(fadeIn, fadeOut);
      w.el.style.opacity = a;
      w.el.style.transform = `translateY(${(1 - fadeIn) * 60 - (1 - fadeOut) * 40}px)`;
      w.el.style.visibility = a < 0.01 ? 'hidden' : 'visible';
    }
  }

  _onVisibility() {
    if (document.hidden) {
      if (this.raf != null) { cancelAnimationFrame(this.raf); this.raf = null; }
    } else {
      // Snap to target so we don't coast through the whole descent on return.
      this.currentP = this.targetP;
      const p = clamp01(this.currentP);
      this.frameState.i = p * (this.count - 1);
      this._render();
      this._updateText(p);
    }
  }

  _resize() {
    // iOS/Android fire 'resize' when the address bar shows/hides: same width, a modest
    // height delta. Re-pinning (ScrollTrigger.refresh) on every such event is what makes
    // the surrounding page fight scroll on touch, so ignore address-bar-only resizes and
    // react only to a width change or a large height change (orientation / real relayout).
    const w = window.innerWidth, h = window.innerHeight;
    const addressBarOnly = w === this._lastW && Math.abs(h - this._lastH) < this._lastH * 0.25;
    this._lastW = w; this._lastH = h;
    if (addressBarOnly) return;

    // Never swap the loaded frame set / pin length at runtime (that would discard
    // this.frames[] and re-stream) — the set is frozen to the device chosen at init.
    // Only dpr (crispness) and the crop aspect adapt.
    this.dpr = Math.min(window.devicePixelRatio || 1, this.useMobileSet ? 1.5 : 2);
    this.targetAspect = this.mqlDesktop.matches ? 16 / 9 : 1; // re-evaluate the 16:9 ↔ 1:1 breakpoint
    // Refresh FIRST: when the descent is pinned, ScrollTrigger holds the section
    // at position:fixed with an explicit size that doesn't track the viewport
    // until a refresh. Measuring the stage before that reads the stale pinned
    // size and leaves the canvas mis-sized (e.g. after a mobile orientation
    // change). Refresh, then measure the settled geometry.
    ScrollTrigger.refresh();
    this._sizeCanvas();
    this._render();
  }
}

const sections = document.querySelectorAll('section.front-page-hero[data-fph]');
if (sections.length) {
  sections.forEach((s) => new FrontPageHero(s).init());
  // Re-measure once late-loading assets settle so pin/start/end are correct.
  window.addEventListener('load', () => ScrollTrigger.refresh());
}

// Scroll-down affordance on the surface band: smooth-scroll to the descent below.
const scrollDown = document.querySelector('[data-fph-scroll]');
if (scrollDown) {
  scrollDown.addEventListener('click', () => {
    const descent = document.querySelector('.front-page-hero[data-fph]');
    const top = descent ? descent.getBoundingClientRect().top + window.scrollY : window.innerHeight;
    window.scrollTo({ top, behavior: reducedMotion() ? 'auto' : 'smooth' });
  });
}